/**
 * Majik's business rules: the numbers, addresses and wording that Make.com
 * hard-codes today (docs/majik/AUTOMATION-LOGIC.md → "Constants & settings").
 * One record, edited by Admins in Settings → Business rules, and read by
 * everything that needs one of them — never typed into a screen
 * (docs/LIVE-DATA.md).
 */
import { z } from 'zod'

/** The alerts a team can be given (docs/majik/AUTOMATION-LOGIC.md → who gets which alert). */
export const alertKinds = [
  'payment_received',
  'invoice_late',
  'client_replied',
  'job_no_cleaner',
  'low_stock',
] as const
export type AlertKind = (typeof alertKinds)[number]

export const alertLabels: Record<AlertKind, string> = {
  payment_received: 'Payment received',
  invoice_late: 'Invoice is 30+ days late',
  client_replied: 'Client texted back',
  job_no_cleaner: 'Job has no cleaner 2 days out',
  low_stock: 'Stock at or below the low mark',
}

export const teamSchema = z.object({
  name: z.string().trim().min(1).max(40),
  /** User ids. */
  members: z.array(z.string()).default([]),
})
export type Team = z.infer<typeof teamSchema>

export const businessRulesSchema = z.object({
  /** Where the work happens. Every job time is shown, and every "today" decided, here. */
  timeZone: z.string().min(1),
  /** The office line customers are asked to call, as it is written in messages. */
  officePhone: z.string().min(1),
  /** The number staff text from, as written in messages. */
  officeTextNumber: z.string().min(1),
  /** The hour (0–23) the day-before notice goes out, the day before the visit. */
  noticeHour: z.number().int().min(0).max(23),
  /** The hour (0–23) each cleaner gets tomorrow's jobs. */
  cleanerListHour: z.number().int().min(0).max(23),
  /** NYC sales tax, in percent (8.875). Added to taxable invoices. */
  salesTaxRate: z.number().min(0).max(100),
  /** A sent invoice still unpaid this many days after sending is overdue. */
  overdueAfterDays: z.number().int().positive(),
  /** Payment reminders, in days after an invoice is sent. Stop once it is paid. */
  paymentReminderDays: z.array(z.number().int().positive()),
  /** Go live: off means every text, email and QuickBooks change is only recorded as "would send". */
  goLive: z.boolean(),
  /**
   * Message wording, with {placeholders}. The day-before notice:
   * {firstName} {when} {address} {officePhone}.
   */
  jobNoticeTemplate: z.string().min(1),
  /** Added to the end of every message to all cleaners ("~Majik Cleaning Services"). */
  textSignature: z.string().max(60).default(''),
  /** "We miss you" reminder: this many days after a location's last invoice (AUTOMATION-LOGIC). */
  reengageAfterDays: z.number().int().positive().default(90),
  /** Stock at or below this is flagged low. Never blocks an order. */
  lowStockAt: z.number().int().min(0).default(4),
  /** Teams, and who is in each. Alerts go to teams, not to named people. */
  teams: z.array(teamSchema).default([]),
  /** For each alert, the teams that get it. */
  alertTeams: z.partialRecord(z.enum(alertKinds), z.array(z.string())).default({}),
  /** Go-live checklist items ticked by hand (the rest are worked out). */
  goLiveChecks: z.array(z.string()).default([]),
  updatedAt: z.iso.datetime(),
})
export type BusinessRules = z.infer<typeof businessRulesSchema>

/** Fill a template's {placeholders}; an unknown one is left as written, so a typo shows. */
export function fillTemplate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match)
}

/** "9:00 AM" — an hour of the day, as the rules screen and the notes say it. */
export function hourText(hour: number): string {
  const suffix = hour >= 12 ? 'PM' : 'AM'
  return `${hour % 12 || 12}:00 ${suffix}`
}
